import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { jwtDecode, type JwtPayload } from 'jwt-decode'

import { DELAY } from '@/constants'
import { useCartStore } from '@/stores/cart/cartStore'
import { useFeedStore } from '@/stores/feed/feedStore'
import { useSearchStore } from '@/stores/feed/searchStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import { ignoreError } from '@/utils/api/errors/ignoreError'
import { queryClient } from '@/utils/api/queryClient'
import { trimRequestData } from '@/utils/api/trimRequestData'

declare module 'axios' {
  export interface AxiosRequestConfig {
    useTimeout?: boolean
    _skipAuth?: boolean
    _skipSanitize?: boolean
    _retry?: boolean
  }
}

type RetriableRequestConfig = InternalAxiosRequestConfig

const ACCESS_TOKEN_KEY = 'access-token-persistent-cache'
const REFRESH_TOKEN_KEY = 'refresh-token'
const REFRESH_THRESHOLD_MS = 30000

let memoryAccessToken: string | null = null
let refreshTimer: ReturnType<typeof setTimeout> | null = null
let isRefreshing = false
let refreshPromise: Promise<string> | null = null

const initTokenCache = () => {
  const cached = localStorage.getItem(ACCESS_TOKEN_KEY)
  if (cached) {
    try {
      const { token, expiry } = JSON.parse(cached)
      if (Date.now() < expiry - 5000) {
        memoryAccessToken = token
        scheduleTokenRefresh(token)
        return
      }
    } catch (e) {
      localStorage.removeItem(ACCESS_TOKEN_KEY)
    }
  }
}

const scheduleTokenRefresh = (token: string) => {
  if (refreshTimer) clearTimeout(refreshTimer)

  try {
    const decoded = jwtDecode<JwtPayload>(token)
    const expiryMs = (decoded.exp ?? 0) * 1000
    const delay = expiryMs - Date.now() - REFRESH_THRESHOLD_MS

    if (delay > 0) {
      refreshTimer = setTimeout(() => {
        refreshTokenFn()
      }, delay)
    } else {
      refreshTokenFn()
    }
  } catch {
    ignoreError()
  }
}

export const setMemoryToken = (token: string | null) => {
  memoryAccessToken = token

  if (token) {
    try {
      const decoded = jwtDecode<JwtPayload>(token)
      const expiry = (decoded.exp ?? 0) * 1000

      localStorage.setItem(
        ACCESS_TOKEN_KEY,
        JSON.stringify({
          token,
          expiry,
        })
      )

      scheduleTokenRefresh(token)
    } catch (e) {
      localStorage.removeItem(ACCESS_TOKEN_KEY)
    }
  } else {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    if (refreshTimer) clearTimeout(refreshTimer)
  }
}

export const getMemoryToken = () => memoryAccessToken

export const publicApi = axios.create({ baseURL: '/api' })
const api = axios.create({ baseURL: '/api' })

const sanitizeRequestText = (config: InternalAxiosRequestConfig) => {
  if (config._skipSanitize) return config

  config.data = trimRequestData(config.data)
  config.params = trimRequestData(config.params)
  return config
}

publicApi.interceptors.request.use(sanitizeRequestText)

const refreshTokenFn = async (): Promise<string> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  isRefreshing = true
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)

  if (!refreshToken) {
    handleLogout()
    throw new Error('Session expired')
  }

  refreshPromise = publicApi
    .post('/auth/refresh-token', { refreshToken }, { _skipAuth: true })
    .then((response) => {
      const { accessToken, refreshToken: newRefreshToken } = response.data.token
      setMemoryToken(accessToken)
      localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken)
      return accessToken
    })
    .catch((err) => {
      handleLogout()
      throw err
    })
    .finally(() => {
      isRefreshing = false
      refreshPromise = null
    })

  return refreshPromise
}

export const getValidMemoryToken = async (): Promise<string | null> => {
  let token = getMemoryToken()
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)

  const isNearlyExpired = () => {
    if (!token) return true
    const decoded = jwtDecode<JwtPayload>(token)
    return (decoded.exp ?? 0) * 1000 < Date.now() + 5000
  }

  if ((!token || isNearlyExpired()) && refreshToken) {
    token = await refreshTokenFn()
  }

  return token
}

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  sanitizeRequestText(config)

  if (import.meta.env.DEV && config.useTimeout !== false) {
    await new Promise((resolve) => setTimeout(resolve, DELAY))
  }

  if (config._skipAuth) return config

  if (config.url?.includes('/cart')) {
    config.headers['Region-Bank'] = config.headers['Region-Bank'] ?? 'NBRB'
  }

  const token = await getValidMemoryToken()

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableRequestConfig | undefined
    if (
      originalRequest &&
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest._skipAuth
    ) {
      originalRequest._retry = true
      const newToken = await refreshTokenFn()
      originalRequest.headers.Authorization = `Bearer ${newToken}`
      return api(originalRequest)
    }
    return Promise.reject(error)
  }
)

export const handleLogout = async () => {
  if (refreshTimer) clearTimeout(refreshTimer)
  memoryAccessToken = null

  localStorage.removeItem('refresh-token')
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem('currentClientId')
  localStorage.removeItem('selectedOrganisationCode')
  localStorage.removeItem('cart-storage')

  useUserInfoStore.getState().setInitialUserInfoStore()
  useCartStore.getState().setInitialCartStore()
  useFeedStore.getState().setInitialFeedStore()
  useSearchStore.getState().setInitialSearchStore()
  queryClient.clear()
}

initTokenCache()

export default api
